import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError, formatDate } from "@/lib/api";

const small = "px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border transition-colors";

export const AdminEvents = ({ onChange }) => {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const load = useCallback(() => api.get("/admin/events", { params: { q } }).then((r) => setList(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8" data-testid="admin-events">
      <div className="flex items-center gap-2 border-b border-white/15 focus-within:border-[#D4AF37] max-w-md">
        <Search className="w-4 h-4 text-[#D4AF37]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca titolo, città, cliente" className="bg-transparent flex-1 py-2.5 text-sm focus:outline-none" data-testid="admin-events-search" />
      </div>
      <div className="mt-6 divide-y divide-white/5 border-y border-white/5">
        {list?.length === 0 && <p className="text-neutral-500 p-10 text-center">Nessun evento</p>}
        {(list || []).map((e) => (
          <div key={e.id} className="py-4 px-2 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`admin-event-${e.id}`}>
            <div className="flex-1 min-w-0">
              <Link to={`/eventi/${e.id}`} className="font-serif text-xl hover:text-[#D4AF37] truncate block">{e.title}</Link>
              <p className="text-[11px] font-mono text-neutral-500">{e.client_name} · {e.city} · {formatDate(e.date)} · {e.applicants_count} candidature · {e.status === "open" ? "aperto" : "chiuso"}</p>
            </div>
            {e.hidden && <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/30 px-2 py-0.5 self-start lg:self-center" data-testid={`admin-event-hidden-badge-${e.id}`}>Nascosto</span>}
            <div className="flex gap-2">
              <button onClick={() => act(() => api.post(`/admin/events/${e.id}/hide`, { value: !e.hidden }), e.hidden ? "Evento visibile" : "Evento nascosto")}
                className={`${small} border-amber-400/40 text-amber-400 hover:bg-amber-400/10`} data-testid={`admin-event-hide-${e.id}`}>{e.hidden ? "Mostra" : "Nascondi"}</button>
              <button onClick={() => setToDelete(e)} className={`${small} border-red-400/40 text-red-400 hover:bg-red-400/10`} data-testid={`admin-event-delete-${e.id}`}>Elimina</button>
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#121418] border-white/10 rounded-sm" data-testid="admin-event-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl font-normal">Eliminare “{toDelete?.title}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">L'evento e tutte le candidature verranno eliminati definitivamente. Il cliente sarà avvisato.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none bg-transparent border-white/20" data-testid="admin-event-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => { act(() => api.delete(`/admin/events/${toDelete.id}`), "Evento eliminato"); setToDelete(null); }}
              className="rounded-none bg-red-500 hover:bg-red-600 text-white" data-testid="admin-event-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
